import React, { useState, useEffect } from 'react';
import { pdf, Text } from '@react-pdf/renderer';
import PDFDocument from './pdf-document';
import { createDailyHeatmap, createDetailedHeatmap, createFifthPageBarChart, createFirstPageBarChart, createHourlyHeatmap, formatTableDate, getBarChartText, getDailyHeatmapText, getDominantPollutant, getFifthBarChartBandName, getHourlyTimestamps, getMonthlyTimestamps, getTablePopulatedPollutantBands, pollutantMap, safariHack } from './utils';
import { DownloadButton } from '../download-measurement';
import { Report } from '../download-measurement/types';
import { Zephyr } from '../../../utils/interface';
import Plot from "plotly.js-dist";

type Props = {
    hasData: boolean;
    selectedReport: Report | null;
    bearerToken: string | null;
    curZephyr: Zephyr;
    reports: Report[];
};

const useSupports = (supportCondition: string) => {
    const [checkResult, setCheckResult] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        setCheckResult(CSS.supports(supportCondition))
    }, [supportCondition]);

    return checkResult;
};

const useDetectSafari = () => {
    const isSafari = useSupports(safariHack);
    return isSafari === true;
};

const generateChartImage = async (isSafari: boolean, isHeatmap: boolean, createChartFn: Function, res3: any, sConfig: any, width: number, height: number, isDetailedHeatmap: boolean, timeStamps?: string[], hourlyTimestamps?: string[]) => {
    const { trace, layout, legendTraces } = timeStamps ? hourlyTimestamps ? createChartFn(res3, sConfig, timeStamps, hourlyTimestamps) : createChartFn(res3, sConfig, timeStamps) : createChartFn(res3, sConfig);
    const chartDiv = document.createElement('div');
    await Plot.newPlot(chartDiv, !isDetailedHeatmap ? [trace] : [trace, ...legendTraces], layout as any);
    if (isSafari && isHeatmap) await Plot.toImage(chartDiv, { format: 'png', width, height });
    return Plot.toImage(chartDiv, { format: 'png', width, height });
}

function formatPollutant(pollutant: string): JSX.Element {
    const splittedPollutant = pollutant.split("(");
    let fullPollutantName: string = `${splittedPollutant[0]}(`, shortPollutantName: string = splittedPollutant[1].split(")")[0];
    let firstPart: string, secondPart: string;

    if (shortPollutantName === "NO2") {
        firstPart = fullPollutantName + "NO";
        secondPart = "2";
    } else if (shortPollutantName === "O3") {
        firstPart = fullPollutantName + "O";
        secondPart = "3";
    } else if (shortPollutantName === "PM2.5") {
        firstPart = fullPollutantName + "PM";
        secondPart = "2.5";
    } else if (shortPollutantName === "PM10") {
        firstPart = fullPollutantName + "PM";
        secondPart = "10";
    } else {
        return <Text style={{ fontFamily: 'Helvetica-Bold' }}>{pollutant}</Text>
    }

    return <Text style={{ fontFamily: 'Helvetica-Bold' }}>{firstPart}<Text style={{ fontSize: 8 }}>{secondPart}</Text>)</Text>
}

function getDetailedHeatmapText(tableDataArr: { date: string, dominantPollutant: string, NO2: number | string, O3: number | string, PM25: number | string, PM10: number | string }[]): JSX.Element {
    const pollutantScores = { 'NO2': 0, 'O3': 0, 'PM2.5': 0, 'PM10': 0 };
    tableDataArr.forEach((data: any) => {
        const { dominantPollutant } = data;
        if (dominantPollutant.includes('NO2')) pollutantScores.NO2 = pollutantScores.NO2 + 1;
        if (dominantPollutant.includes('O3')) pollutantScores.O3 = pollutantScores.O3 + 1;
        if (dominantPollutant.includes('PM2.5')) pollutantScores['PM2.5'] = pollutantScores['PM2.5'] + 1;
        if (dominantPollutant.includes('PM10')) pollutantScores.PM10 = pollutantScores.PM10 + 1;
    })
    const sortedPollutants = Object.entries(pollutantScores)
        .sort((a, b) => b[1] - a[1]);

    const maxVal = sortedPollutants[0][1];
    const dominant = sortedPollutants
        .filter(([, value]) => value === maxVal)
        .map(([key]) => pollutantMap[key]);

    const remaining = sortedPollutants
        .filter(([, value]) => value < maxVal)
        .map(([key]) => pollutantMap[key]);

    if (dominant.length === 4) {
        return (
            <Text style={{ fontSize: 11 }}>
                Based on total exposure, {dominant.map((pollutant, index) => (
                    <>{index > 0 && ", "}{formatPollutant(pollutant)}</>
                ))} have been the dominant pollutants across the month.
            </Text>
        );
    }

    return (
        <Text style={{ fontSize: 11, color: "#000", lineHeight: 1.5, letterSpacing: -0.32 }}>
            Based on total exposure, {dominant.map((pollutant, index) => (
                <Text key={pollutant}>{index > 0 ? (index === dominant.length - 1 ? " and " : ", ") : ""}{formatPollutant(pollutant)}</Text>
            ))} {dominant.length > 1 ? "have" : "has"} been the dominant pollutant{dominant.length > 1 ? "s" : ""} across the month, followed by {remaining.map((pollutant, index) => (
                <Text key={pollutant}>{index > 0 ? (index === remaining.length - 1 ? " and " : ", ") : ""}{formatPollutant(pollutant)}</Text>
            ))}.
        </Text>
    );
}

const Reporting = ({ hasData, selectedReport, bearerToken, curZephyr, reports }: Props) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDownloadButton, setShowDownloadButton] = useState(true);
    const [pdfUrl, setPdfUrl] = useState('');
    const { alias, zNumber } = curZephyr;
    const isSafari = useDetectSafari();

    const generateReportData = async () => {
        setIsDownloading(true);
        setShowDownloadButton(false);

        try {
            const { reportDate, measurementData } = selectedReport as Report;

            const { dateTime, NO2, O3, particulatePM25, particulatePM10 } = measurementData;
            const postBody = {
                timeStamps: dateTime.data,
                pollutants: [{ species: 'NO2', unit: NO2.header.units, values: NO2.data }, { species: 'O3', unit: O3.header.units, values: O3.data }, { species: 'particulatePM25', unit: particulatePM25.header.units, values: particulatePM25.data }, { species: 'particulatePM10', unit: particulatePM10.header.units, values: particulatePM10.data }]
            }

            const [res2, res3, res4] = await Promise.all([

                fetch(`https://service.earthsense.co.uk/myair/api/v1/reports/events/${reportDate.split('T')[0]}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    },
                }).then((res) => res.json()),

                fetch('https://service.earthsense.co.uk/myair/api/v1/aqi/convertdata/eu?generate_daily_aqis=true', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    },
                    body: JSON.stringify(postBody),
                }).then((res) => res.json()),

                fetch('https://service.earthsense.co.uk/myair/api/v1/aqi/eu', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${bearerToken}`
                    },
                }).then((res) => res.json()),
            ]);

            const { AQIs, timeStamps: aqiTimeStamps } = res3.AQIDataOutputs.find((item: any) => item.label === 'daily');
            let NO2Data = NO2.data;
            let O3Data = O3.data;
            let PM25Data = particulatePM25.data;
            let PM10Data = particulatePM10.data;
            let NO2Bands = AQIs.find((item: { AQISpecies: string, band: number[] }) => item.AQISpecies === 'NO2').band;
            let O3Bands = AQIs.find((item: { AQISpecies: string, band: number[] }) => item.AQISpecies === 'O3').band;
            let PM25Bands = AQIs.find((item: { AQISpecies: string, band: number[] }) => item.AQISpecies === 'particulatePM25').band;
            let PM10Bands = AQIs.find((item: { AQISpecies: string, band: number[] }) => item.AQISpecies === 'particulatePM10').band;
            const tableDataArr: { date: string, dominantPollutant: string, NO2: number | string, O3: number | string, PM25: number | string, PM10: number | string }[] = [];
            const monthlyTimeStamps = getMonthlyTimestamps(reportDate);
            const hourlyTimestamps = getHourlyTimestamps(reportDate);
            const isDataMissing = dateTime.data.length !== hourlyTimestamps.length;
            if (isDataMissing) {
                const { updatedNO2Band, updatedO3Band, updatedPM25Band, updatedPM10Band } = getTablePopulatedPollutantBands(dateTime.data, hourlyTimestamps, NO2Data, O3Data, PM25Data, PM10Data);
                NO2Data = updatedNO2Band;
                O3Data = updatedO3Band;
                PM25Data = updatedPM25Band;
                PM10Data = updatedPM10Band;
            }
            if (aqiTimeStamps.length !== monthlyTimeStamps.length) {
                const { updatedNO2Band, updatedO3Band, updatedPM25Band, updatedPM10Band } = getTablePopulatedPollutantBands(aqiTimeStamps, monthlyTimeStamps, NO2Bands, O3Bands, PM25Bands, PM10Bands);
                NO2Bands = updatedNO2Band;
                O3Bands = updatedO3Band;
                PM25Bands = updatedPM25Band;
                PM10Bands = updatedPM10Band;
            }
            for (let i = 1; i <= (isDataMissing ? hourlyTimestamps.length : dateTime.data.length) / 24; i++) {
                const curDay = (isDataMissing ? hourlyTimestamps : dateTime.data).slice((i - 1) * 24, i * 24);
                const NO2Average = NO2Data.slice((i - 1) * 24, i * 24).every((val: number | null) => val === null) ? 'N/A' : NO2Data.slice((i - 1) * 24, i * 24).reduce((acc: number, val: number | null) => { return typeof val === 'number' ? acc + val : acc; }, 0) / NO2Data.slice((i - 1) * 24, i * 24).filter((val: number | null) => typeof val === 'number').length;
                const O3Average = O3Data.slice((i - 1) * 24, i * 24).every((val: number | null) => val === null) ? 'N/A' : O3Data.slice((i - 1) * 24, i * 24).reduce((acc: number, val: number | null) => { return typeof val === 'number' ? acc + val : acc; }, 0) / O3Data.slice((i - 1) * 24, i * 24).filter((val: number | null) => typeof val === 'number').length;
                const PM25Average = PM25Data.slice((i - 1) * 24, i * 24).every((val: number | null) => val === null) ? 'N/A' : PM25Data.slice((i - 1) * 24, i * 24).reduce((acc: number, val: number | null) => { return typeof val === 'number' ? acc + val : acc; }, 0) / PM25Data.slice((i - 1) * 24, i * 24).filter((val: number | null) => typeof val === 'number').length;
                const PM10Average = PM10Data.slice((i - 1) * 24, i * 24).every((val: number | null) => val === null) ? 'N/A' : PM10Data.slice((i - 1) * 24, i * 24).reduce((acc: number, val: number | null) => { return typeof val === 'number' ? acc + val : acc; }, 0) / PM10Data.slice((i - 1) * 24, i * 24).filter((val: number | null) => typeof val === 'number').length;
                const NO2Max = typeof NO2Bands[i - 1] === 'number' ? NO2Bands[i - 1] : 'N/A';
                const O3Max = typeof O3Bands[i - 1] === 'number' ? O3Bands[i - 1] : 'N/A';
                const PM25Max = typeof PM25Bands[i - 1] === 'number' ? PM25Bands[i - 1] : 'N/A';
                const PM10Max = typeof PM10Bands[i - 1] === 'number' ? PM10Bands[i - 1] : 'N/A';
                const dominantPollutant = getDominantPollutant(NO2Max, O3Max, PM25Max, PM10Max);
                tableDataArr.push({
                    date: formatTableDate(curDay[0].split('T')[0]),
                    dominantPollutant,
                    NO2: typeof NO2Average === 'number' ? String(NO2Average).includes('.') ? NO2Average.toFixed(1) : NO2Average : NO2Average,
                    O3: typeof O3Average === 'number' ? String(O3Average).includes('.') ? O3Average.toFixed(1) : O3Average : O3Average,
                    PM25: typeof PM25Average === 'number' ? String(PM25Average).includes('.') ? PM25Average.toFixed(1) : PM25Average : PM25Average,
                    PM10: typeof PM10Average === 'number' ? String(PM10Average).includes('.') ? PM10Average.toFixed(1) : PM10Average : PM10Average,
                });
            }
            const { text } = reports.find((textObj: any) => textObj.reportDate === selectedReport?.reportDate) as Report;

            let reformattedEvents = [];
            if (res2.length) {
                reformattedEvents = res2.map((event: { date: string, link: string, location: string, time: string, name: string, organiser: string }) => ({
                    name: event.name,
                    dateLocation: event.location ? `${event.date} | ${event.location}` : `${event.date}`,
                    link: event.link,
                }));
            }
            const { speciesBands } = res4;
            const sConfig: { [key: number]: { name: string, color: string } } = {};
            speciesBands.find((obj: { species: string, unit: string, band: any[] }) => obj.species.toLowerCase() === 'aqi').bands.forEach((bandObj: any) => {
                const { number, name, colorHex: color } = bandObj;
                sConfig[number] = { name, color };
            })
            const [firstPageBarChart, dailyHeatmap, detailedHeatmap, fifthPageBarChart, hourlyHeatmap] = await Promise.all([
                generateChartImage(isSafari, false, createFirstPageBarChart, res3, sConfig, 1550, 600, false),
                generateChartImage(isSafari, true, createDailyHeatmap, res3, sConfig, 1350, 800, false, monthlyTimeStamps),
                generateChartImage(isSafari, true, createDetailedHeatmap, res3, sConfig, 1650, 800, true, monthlyTimeStamps),
                generateChartImage(isSafari, false, createFifthPageBarChart, res3, sConfig, 2000, 1050, false),
                generateChartImage(isSafari, true, createHourlyHeatmap, res3, sConfig, 1550, 1250, false, monthlyTimeStamps, hourlyTimestamps)
            ]);
            const firstPageBarChartText = getBarChartText(res3, sConfig);
            const fourthPageDailyHeatmapText = getDailyHeatmapText(res3, sConfig);
            const fourthPageDetailedHeatmapText = getDetailedHeatmapText(tableDataArr);
            const fifthPageBandName: string = getFifthBarChartBandName(res3, sConfig);
            const blob = await pdf(<PDFDocument
                freeText={text}
                events={reformattedEvents}
                tableData={tableDataArr}
                alias={alias ?? zNumber}
                zNumber={zNumber}
                selectedReportDate={selectedReport?.reportDate}
                onRenderComplete={() => {
                    const url = URL.createObjectURL(blob);
                    setPdfUrl(url);
                }}
                firstPageBarChart={firstPageBarChart}
                dailyHeatmap={dailyHeatmap}
                detailedHeatmap={detailedHeatmap}
                fifthPageBarChart={fifthPageBarChart}
                hourlyHeatmap={hourlyHeatmap}
                firstPageBarChartText={firstPageBarChartText}
                fourthPageDailyHeatmapText={fourthPageDailyHeatmapText}
                fourthPageDetailedHeatmapText={fourthPageDetailedHeatmapText}
                fifthPageBandName={fifthPageBandName}
            />).toBlob();
        } catch (error) {
            console.error('Error generating report:', error);
            setIsDownloading(false);
            setShowDownloadButton(true);
        }
    };


    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    useEffect(() => {
        if (pdfUrl) {
            if (isMobile) {
                window.location.href = pdfUrl;
            } else {
                window.open(pdfUrl, '_blank');
            }

            setIsDownloading(false);
            setShowDownloadButton(true);
            setPdfUrl('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfUrl]);

    return (
        <div>
            {showDownloadButton && (
                <DownloadButton
                    type="button"
                    onClick={generateReportData}
                    disabled={!hasData || !selectedReport}
                >
                    Download
                </DownloadButton>
            )}

            {isDownloading && (
                <DownloadButton type="button" disabled>
                    Generating...
                </DownloadButton>
            )}
        </div>
    );
};

export default Reporting;